import { createFileRoute } from "@tanstack/react-router";

function count(n: number) {
  return Array.from({ length: n }, (_, i) => i);
}

function pseudoRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

const stars = count(100).map((key) => ({
  key,
  left: `${pseudoRandom(key * 17.31 + 1) * 100}%`,
  top: `${pseudoRandom(key * 17.31 + 2) * 60}%`,
  opacity: pseudoRandom(key * 17.31 + 3) * 0.8 + 0.2,
  animation: `neonPulse ${2 + pseudoRandom(key * 17.31 + 4) * 3}s ease-in-out infinite`,
  animationDelay: `${pseudoRandom(key * 17.31 + 5) * 2}s`,
}));

export const Route = createFileRoute("/synthwave")({
  component: SynthwaveHomePage,
});

/**
 * Design 2: Synthwave Retro-Futuristic
 *
 * Aesthetic: 80s sci-fi, neon-drenched future
 * - Cyan and magenta neon on deep purple/black
 * - Perspective grid patterns
 * - Glowing effects and animated outlines
 * - Chrome and holographic accents
 */

function SynthwaveHomePage() {
  return (
    <div className="min-h-screen bg-[#0D0221] text-white overflow-hidden relative">
      {/* Google Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Rajdhani:wght@300;400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        .font-cyber { font-family: 'Orbitron', sans-serif; }
        .font-tech { font-family: 'Rajdhani', sans-serif; }

        @keyframes gridScroll {
          0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
          100% { transform: perspective(500px) rotateX(60deg) translateY(50px); }
        }

        @keyframes neonPulse {
          0%, 100% { opacity: 1; filter: drop-shadow(0 0 20px currentColor); }
          50% { opacity: 0.8; filter: drop-shadow(0 0 40px currentColor); }
        }

        @keyframes scanline {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }

        @keyframes glitch {
          0%, 90%, 100% { transform: translate(0); }
          92% { transform: translate(-2px, 1px); }
          94% { transform: translate(2px, -1px); }
          96% { transform: translate(-1px, 2px); }
          98% { transform: translate(1px, -2px); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }

        @keyframes borderGlow {
          0%, 100% {
            box-shadow: 0 0 5px #00FFFF, 0 0 10px #00FFFF, 0 0 20px #00FFFF, inset 0 0 5px rgba(0, 255, 255, 0.1);
          }
          50% {
            box-shadow: 0 0 10px #FF00FF, 0 0 20px #FF00FF, 0 0 40px #FF00FF, inset 0 0 10px rgba(255, 0, 255, 0.1);
          }
        }

        .animate-grid { animation: gridScroll 3s linear infinite; }
        .animate-neon { animation: neonPulse 2s ease-in-out infinite; }
        .animate-scanline { animation: scanline 8s linear infinite; }
        .animate-glitch { animation: glitch 3s ease-in-out infinite; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-border-glow { animation: borderGlow 4s ease-in-out infinite; }

        .neon-cyan {
          color: #00FFFF;
          text-shadow: 0 0 5px rgba(0, 255, 255, 0.5), 0 0 10px rgba(0, 255, 255, 0.3);
        }

        .neon-magenta {
          color: #FF00FF;
          text-shadow: 0 0 5px rgba(255, 0, 255, 0.5), 0 0 10px rgba(255, 0, 255, 0.3);
        }

        .neon-yellow {
          color: #FFFF00;
          text-shadow: 0 0 5px rgba(255, 255, 0, 0.5);
        }

        .gradient-chrome {
          background: linear-gradient(180deg, #FFF 0%, #AAA 20%, #666 40%, #999 60%, #FFF 80%, #888 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .bg-grid-perspective {
          background-image:
            linear-gradient(to right, rgba(255, 0, 255, 0.3) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 255, 255, 0.3) 1px, transparent 1px);
          background-size: 50px 50px;
        }
      `}</style>

      {/* Scanline Overlay */}
      <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden opacity-[0.03]">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500 to-transparent h-[200%] animate-scanline"></div>
      </div>

      {/* Perspective Grid Background */}
      <div className="fixed inset-0 overflow-hidden">
        {/* Stars */}
        <div className="absolute inset-0 z-10">
          {stars.map(({ key, ...style }) => (
            <div
              key={key}
              className="absolute w-1 h-1 bg-white rounded-full"
              style={style}
            />
          ))}
        </div>

        {/* Gradient Sky */}
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-[#0D0221] via-[#1A0533] to-[#2D1B4E]"></div>

        {/* Sun */}
        <div className="absolute z-20 left-1/2 top-[40%] -translate-x-1/2 w-[400px] h-[400px] rounded-full bg-gradient-to-b from-[#FF6B35] via-[#FF00FF] to-[#FF00FF]/0 opacity-80 blur-sm"></div>
        <div className="absolute z-20 left-1/2 top-[40%] -translate-x-1/2 w-[380px] h-[380px] rounded-full overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFD93D] via-[#FF6B6B] to-[#C44569]"></div>
          {/* Sun lines */}
          {count(8).map((i) => (
            <div
              key={i}
              className="absolute w-full h-[3px] bg-[#0D0221]/80"
              style={{ top: `${30 + i * 10}%` }}
            ></div>
          ))}
        </div>

        {/* Grid Floor */}
        <div className="absolute z-20 bottom-0 left-0 right-0 h-[50vh] origin-bottom">
          <div
            className="absolute inset-0 bg-grid-perspective animate-grid"
            style={{ transform: "perspective(500px) rotateX(60deg)" }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D0221] via-transparent to-transparent"></div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="py-6 px-8">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="relative">
                <img
                  src="/web-app-manifest-512x512.png"
                  alt="Goose"
                  width={50}
                  height={50}
                  className="relative z-10"
                  style={{ filter: "drop-shadow(0 0 10px #00FFFF)" }}
                />
              </div>
              <span className="font-cyber text-2xl neon-cyan">GOOSE</span>
            </div>
            <div className="hidden md:flex items-center gap-8 font-tech text-sm tracking-wider">
              <span className="text-cyan-400/60">SYS.ONLINE</span>
              <span className="neon-magenta">v2.026</span>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-cyan-400 animate-neon"></span>
                <span className="text-cyan-400">LAB EXPERIMENT</span>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-12 md:py-24 px-8">
          <div className="max-w-7xl mx-auto text-center">
            {/* Floating Badge */}
            <div className="inline-block mb-8 animate-float">
              <div className="font-tech text-sm tracking-[0.3em] px-6 py-2 border border-cyan-400/50 rounded-full bg-cyan-400/5 neon-cyan animate-border-glow">
                ◈ THE FUTURE OF RECRUITING ◈
              </div>
            </div>

            {/* Main Title */}
            <h1 className="font-cyber text-5xl md:text-8xl lg:text-9xl font-black tracking-wider mb-6 animate-glitch">
              <span className="gradient-chrome">THE ACCOUNT</span>
              <br />
              <span className="gradient-chrome">MANAGER&apos;S</span>
              <br />
              <span className="neon-magenta animate-neon">AI WINGMAN</span>
            </h1>

            {/* Subtitle */}
            <p className="font-tech text-xl md:text-2xl text-cyan-300/80 max-w-2xl mx-auto mb-12 tracking-wide">
              Assess Talent fit before engaging the Hiring Manager.
              <br />
              <span className="neon-yellow">
                Opportunity → Evaluation in seconds.
              </span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="font-cyber text-sm tracking-widest px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 text-[#0D0221] font-bold hover:from-cyan-400 hover:to-cyan-300 transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,255,255,0.5)]">
                ▶ INITIALIZE SYSTEM
              </button>
              <button
                className="font-cyber text-sm tracking-widest px-8 py-4 border-2 border-magenta-500 neon-magenta hover:bg-[#FF00FF]/10 transition-all duration-300"
                style={{ borderColor: "#FF00FF" }}
              >
                ◇ VIEW DEMO
              </button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 md:py-24 px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="font-tech text-sm tracking-[0.5em] neon-cyan mb-4">
                {"// SYSTEM CAPABILITIES"}
              </div>
              <h2 className="font-cyber text-3xl md:text-5xl gradient-chrome">
                FEATURE MATRIX
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "◈",
                  title: "INSTANT PARSING",
                  desc: "Auto-extract structured requirements from any job description in ~30 seconds.",
                  color: "cyan",
                },
                {
                  icon: "◆",
                  title: "CONTEXTUAL FIT",
                  desc: "LLM-powered context understanding, not keyword matching.",
                  color: "magenta",
                },
                {
                  icon: "◇",
                  title: "CONVERSATIONAL AI",
                  desc: "Refine requirements through natural dialogue. No forms.",
                  color: "yellow",
                },
                {
                  icon: "▣",
                  title: "TRANSPARENT LOGIC",
                  desc: "See exactly where candidates excel and where gaps exist.",
                  color: "cyan",
                },
                {
                  icon: "◉",
                  title: "SALESFORCE SYNC",
                  desc: "Pull opportunities directly. Update profiles seamlessly.",
                  color: "magenta",
                },
                {
                  icon: "◎",
                  title: "REAL-TIME UPDATE",
                  desc: "Scores recalculate as new information flows in.",
                  color: "yellow",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="group relative p-6 border border-cyan-400/30 bg-[#0D0221]/80 backdrop-blur-sm hover:border-cyan-400 transition-all duration-500"
                  style={{
                    clipPath:
                      "polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))",
                  }}
                >
                  {/* Corner accents */}
                  <div className="absolute top-0 right-0 w-[20px] h-[1px] bg-cyan-400/50 origin-right rotate-45 translate-x-[-7px] translate-y-[10px]"></div>
                  <div className="absolute bottom-0 left-0 w-[20px] h-[1px] bg-cyan-400/50 origin-left rotate-45 translate-x-[7px] translate-y-[-10px]"></div>

                  <div
                    className={`font-cyber text-4xl mb-4 ${feature.color === "cyan" ? "neon-cyan" : feature.color === "magenta" ? "neon-magenta" : "neon-yellow"} animate-neon`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="font-cyber text-lg mb-2 text-white group-hover:text-cyan-400 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="font-tech text-cyan-300/60 text-sm leading-relaxed">
                    {feature.desc}
                  </p>

                  {/* Hover glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/0 to-magenta-500/0 group-hover:from-cyan-400/5 group-hover:to-magenta-500/5 transition-all duration-500 pointer-events-none"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24 px-8 relative">
          {/* Decorative line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent hidden lg:block"></div>

          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <div className="font-tech text-sm tracking-[0.5em] neon-magenta mb-4">
                {"// OPERATIONAL FLOW"}
              </div>
              <h2 className="font-cyber text-3xl md:text-5xl gradient-chrome">
                SYSTEM PROTOCOL
              </h2>
            </div>

            <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-12">
              {[
                {
                  num: "01",
                  title: "IMPORT OPPORTUNITY",
                  desc: "Auto-import from Connected or paste any job description. Requirements parsed instantly.",
                },
                {
                  num: "02",
                  title: "REFINE VIA DIALOGUE",
                  desc: "Clarify ambiguous requirements through conversation. 'Python only'—done.",
                },
                {
                  num: "03",
                  title: "EVALUATE CANDIDATES",
                  desc: "Pull profiles for instant contextual fit analysis with gap identification.",
                },
                {
                  num: "04",
                  title: "ADD INSIGHTS",
                  desc: "New cert from an email? Goose incorporates it, recalculates fit immediately.",
                },
              ].map((step, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-6 ${i % 2 === 1 ? "lg:flex-row-reverse lg:text-right" : ""}`}
                >
                  <div className="shrink-0">
                    <div className="font-cyber text-5xl md:text-7xl font-black neon-cyan animate-neon">
                      {step.num}
                    </div>
                  </div>
                  <div className="pt-2">
                    <h3 className="font-cyber text-xl md:text-2xl text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="font-tech text-cyan-300/60">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: "~30s", label: "PARSE TIME" },
                { value: "100%", label: "TRANSPARENT" },
                { value: "∞", label: "SCALABLE" },
                { value: "24/7", label: "AVAILABLE" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="text-center p-6 border border-magenta-500/30 bg-[#0D0221]/50"
                  style={{ borderColor: "rgba(255, 0, 255, 0.3)" }}
                >
                  <div className="font-cyber text-3xl md:text-4xl neon-magenta animate-neon mb-2">
                    {stat.value}
                  </div>
                  <div className="font-tech text-xs tracking-widest text-cyan-400/60">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lab Experiment Section */}
        <section className="py-16 px-8">
          <div className="max-w-3xl mx-auto">
            <div className="relative p-8 md:p-12 border-2 border-cyan-400/50 bg-[#0D0221]/90 backdrop-blur-sm animate-border-glow">
              {/* Corner decorations */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-cyan-400"></div>
              <div
                className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-magenta-500"
                style={{ borderColor: "#FF00FF" }}
              ></div>
              <div
                className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-magenta-500"
                style={{ borderColor: "#FF00FF" }}
              ></div>
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-cyan-400"></div>

              <div className="font-tech text-sm tracking-[0.3em] neon-yellow mb-4">
                ⚠ LAB EXPERIMENT
              </div>
              <h3 className="font-cyber text-2xl md:text-3xl text-white mb-6">
                LEARNING OBJECTIVES
              </h3>

              <div className="space-y-4 font-tech text-cyan-300/80">
                <p className="flex items-start gap-3">
                  <span className="neon-cyan">▸</span>
                  <span>
                    Data storytelling vs. informal recruiter knowledge
                  </span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="neon-magenta">▸</span>
                  <span>LLM-based fit evaluation beyond keyword matching</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="neon-yellow">▸</span>
                  <span>
                    Conversational capture of insights → Connected sync
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-8 border-t border-cyan-400/20">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img
                src="/web-app-manifest-512x512.png"
                alt="Goose"
                width={32}
                height={32}
                style={{ filter: "drop-shadow(0 0 5px #00FFFF)" }}
              />
              <span className="font-cyber text-sm neon-cyan">GOOSE</span>
            </div>
            <div className="font-tech text-cyan-400/60 text-sm tracking-wider">
              THE ACCOUNT MANAGER&apos;S AI WINGMAN
            </div>
            <div className="font-tech text-xs">
              <span className="neon-magenta">◈</span>
              <span className="text-cyan-400/40 ml-2">A LAB EXPERIMENT</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
